import { getApiUrl } from '../config/api';

export type SubscriptionPlan = 'monthly' | 'quarterly' | 'biannual';
export type PackageKey = 'jobPost' | 'quickSearch' | 'small' | 'medium' | 'large';

export interface CurrentPackage {
    plan: SubscriptionPlan;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    subscriptionId: string;
    isActive: boolean;
}

export interface StoredUser {
    id?: string;
    _id?: string;
    username: string;
    fullName: string;
    email: string;
    emailVerified?: boolean;
    role: string;
    userType?: 'staff' | 'employer';
    subscriptionPlan?: SubscriptionPlan | null;
    subscriptionStatus?: string;
    currentPeriodEnd?: string | null;
    currentPackage?: CurrentPackage;
    jobPostsCount?: number;
    quickSearchCount?: number;
    activeQuickSearchExpiry?: string | null;
    quickSearchAccessGranted?: boolean;
}

type ApiErrorPayload = {
    message?: string;
    error?: string;
    statusCode?: number;
};

const staffRoles = [
    'barman', 'bartender', 'waiter', 'waitress', 'chef',
    'sous chef', 'cook', 'dishwasher', 'host/hostess',
    'sommelier', 'busser', 'kitchen porter', 'line cook', 'pastry chef'
];

const adminRoles = ['admin', 'superadmin'];

export const getStoredUser = (): StoredUser | null => {
    const rawUser = localStorage.getItem('user');

    if (!rawUser) {
        return null;
    }

    try {
        return JSON.parse(rawUser) as StoredUser;
    } catch {
        return null;
    }
};

export const setStoredUser = (user: StoredUser): void => {
    localStorage.setItem('user', JSON.stringify(user));
};

export const isEmployerUser = (user: StoredUser | null): boolean => {
    if (!user) {
        return false;
    }

    if (user.userType) {
        return user.userType === 'employer';
    }

    const normalizedRole = user.role?.toLowerCase() || '';

    const isAdmin = adminRoles.some(role => normalizedRole.includes(role));
    const isStaff = staffRoles.some(role => normalizedRole.includes(role));

    return !isAdmin && !isStaff;
};

const readJsonSafe = async <T>(response: Response): Promise<T | null> => {
    try {
        return (await response.json()) as T;
    } catch {
        return null;
    }
};

const mapSubscriptionApiError = (response: Response, data: ApiErrorPayload | null): string => {
    const apiMessage = data?.message || data?.error;

    if (apiMessage) {
        return apiMessage;
    }

    if (response.status === 400) {
        return 'Invalid package request. Please check the selected package and try again.';
    }

    if (response.status === 401) {
        return 'Your session expired. Please log in again.';
    }

    if (response.status === 403) {
        return 'You are not allowed to perform this subscription action.';
    }

    if (response.status === 404) {
        return 'Subscription endpoint not found.';
    }

    if (response.status >= 500) {
        return 'Subscription service is temporarily unavailable. Please try again later.';
    }

    return 'Subscription request failed.';
};

export const createSubscriptionCheckout = async (
    token: string,
    packageKey: PackageKey
): Promise<string> => {
    const response = await fetch(getApiUrl('subscriptions/create-checkout'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ packageKey })
    });

    const data = await readJsonSafe<{ checkoutUrl?: string } & ApiErrorPayload>(response);

    if (!response.ok) {
        throw new Error(mapSubscriptionApiError(response, data));
    }

    if (!data?.checkoutUrl) {
        throw new Error('Checkout URL was not returned by API');
    }

    return data.checkoutUrl;
};

export interface MySubscriptionResponse {
    plan?: SubscriptionPlan;
    status: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    message?: string;
}

export const getMySubscription = async (token: string): Promise<MySubscriptionResponse> => {
    const response = await fetch(getApiUrl('subscriptions/my-subscription'), {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    const data = await readJsonSafe<MySubscriptionResponse & ApiErrorPayload>(response);

    if (!response.ok) {
        throw new Error(mapSubscriptionApiError(response, data));
    }

    if (!data) {
        throw new Error('Invalid subscription response from server');
    }

    return data;
};

export const refreshUserProfile = async (token: string): Promise<StoredUser> => {
    const response = await fetch(getApiUrl('users/profile'), {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    const data = await readJsonSafe<StoredUser & ApiErrorPayload>(response);

    if (!response.ok) {
        throw new Error(mapSubscriptionApiError(response, data));
    }

    if (!data || !data.email || !data.role) {
        throw new Error('Invalid profile response from server');
    }

    setStoredUser(data);
    return data;
};

export const activateQuickSearch = async (token: string): Promise<void> => {
    const response = await fetch(getApiUrl('subscriptions/activate-quicksearch'), {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    const data = await readJsonSafe<ApiErrorPayload>(response);

    if (!response.ok) {
        throw new Error(mapSubscriptionApiError(response, data));
    }

    // Refresh user profile to update activeQuickSearchExpiry
    await refreshUserProfile(token);
};
